import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronDown, ChevronUp, RotateCcw } from "lucide-react";
import type { VINUnit } from "@/hooks/useUnits";
import rvImage from "@/assets/jayco-interior.png";
import terrainImage from "@/assets/terrain-2026.png";

const unitImages: Record<string, string> = {
  "Terrain": terrainImage,
};

interface GarageHeroCardProps {
  unit?: VINUnit;
  isLoading?: boolean;
}

const expandedSpecs = [
  { label: "Gross Vehicle Weight Rating", value: "9,050 lbs" },
  { label: "Gross Combined Weight Rating", value: "13,930 lbs" },
  { label: "Exterior Length", value: "23'6\"" },
  { label: "Exterior Height", value: "10'1\"" },
  { label: "Exterior Width", value: "6'10\"" },
  { label: "Fresh Water", value: "21 gals" },
  { label: "Gray Water", value: "19 gals" },
  { label: "Black", value: "5.5 gals" },
  { label: "Fuel Tank", value: "24 gals" },
];

export function GarageHeroCard({ unit, isLoading }: GarageHeroCardProps) {
  const [isFlipped, setIsFlipped] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  if (isLoading) {
    return (
      <Card className="mx-4 mt-4 overflow-hidden">
        <Skeleton className="h-48 w-full" />
        <div className="p-4 space-y-2">
          <Skeleton className="h-6 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      </Card>
    );
  }

  if (!unit) {
    return (
      <Card className="mx-4 mt-4 p-6 text-center">
        <p className="text-muted-foreground">No unit found. Register your RV to get started.</p>
      </Card>
    );
  }

  const displayName = `${unit.modelYear} ${unit.model || unit.modelLine}`;
  const warrantyActive = unit.warrantyStatus === "Active";

  const handleFlipToBack = () => {
    setIsFlipped(true);
  };

  const handleFlipToFront = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsFlipped(false);
    setIsExpanded(false);
  };

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded((current) => !current);
  };

  return (
    <div className="mx-4 mt-4 flip-card" data-flipped={isFlipped} data-expanded={isExpanded}>
      <div className="flip-card-inner">
        {/* Front of card */}
        <Card
          className="flip-card-front overflow-hidden border-0 shadow-lg cursor-pointer active:scale-[0.98] transition-transform duration-200"
          onClick={handleFlipToBack}
        >
          <div className="relative h-48 bg-muted">
            <img
              src={unitImages[unit.model || ""] || rvImage}
              alt={displayName}
              className="w-3/4 h-3/4 object-contain mx-auto"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground/70 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-4">
              <h2 className="text-lg font-bold text-card">{displayName}</h2>
              <p className="text-xs text-card/80 font-mono tracking-wide mt-0.5">
                VIN: {unit.vin.slice(0, 11)}...
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 px-4 py-3">
            <Badge
              variant={warrantyActive ? "default" : "secondary"}
              className={warrantyActive ? "bg-success text-success-foreground" : ""}
            >
              Warranty: {unit.warrantyStatus}
            </Badge>
            <Badge variant="outline" className="text-muted-foreground">
              {unit.unitType === "TT" ? "Travel Trailer" : unit.unitType === "FW" ? "Fifth Wheel" : unit.unitType}
            </Badge>
          </div>
        </Card>

        {/* Back of card */}
        <Card
          className={`flip-card-back overflow-hidden border-0 shadow-lg transition-transform duration-200 ${!isExpanded ? "cursor-pointer active:scale-[0.98]" : ""}`}
          style={{ backgroundColor: "#0056b8" }}
          onClick={!isExpanded ? handleFlipToFront : undefined}
        >
          <div className="flex h-full flex-col px-5 pb-5 pt-4 text-white">
            <div className="flex justify-end">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-white/70 hover:text-white hover:bg-white/10"
                onClick={handleFlipToFront}
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
            </div>
            <div className="space-y-2.5">
              <div className="flex items-start justify-between gap-4 border-b border-white/20 pb-2">
                <span className="text-sm font-medium text-white/70">Make</span>
                <span className="text-sm font-semibold text-right leading-tight">Jayco</span>
              </div>
              <div className="flex items-start justify-between gap-4 border-b border-white/20 pb-2">
                <span className="text-sm font-medium text-white/70">Model</span>
                <span className="text-sm font-semibold text-right leading-tight">{unit.model || unit.modelLine}</span>
              </div>
              <div className="flex items-start justify-between gap-4 border-b border-white/20 pb-2">
                <span className="text-sm font-medium text-white/70">Year</span>
                <span className="text-sm font-semibold text-right leading-tight">{unit.modelYear}</span>
              </div>
              <div className="flex items-start justify-between gap-4 border-b border-white/20 pb-2">
                <span className="text-sm font-medium text-white/70">Floorplan</span>
                <span className="text-sm font-semibold text-right leading-tight">{unit.floorplan || "N/A"}</span>
              </div>
              <div className="flex items-start justify-between gap-4 pb-1">
                <span className="text-sm font-medium text-white/70">VIN</span>
                <span className="max-w-[11rem] break-all text-right text-xs font-mono font-semibold leading-tight">{unit.vin}</span>
              </div>
            </div>

            {/* Expanded specs */}
            <div
              className="overflow-hidden transition-all duration-400 ease-in-out"
              style={{
                maxHeight: isExpanded ? "600px" : "0px",
                opacity: isExpanded ? 1 : 0,
              }}
            >
              <div className="mt-3 space-y-2.5 border-t border-white/20 pt-3">
                {expandedSpecs.map((spec) => (
                  <div key={spec.label} className="flex items-start justify-between gap-4 border-b border-white/20 pb-2 last:border-b-0">
                    <span className="text-sm font-medium text-white/70">{spec.label}</span>
                    <span className="text-sm font-semibold text-right leading-tight">{spec.value}</span>
                  </div>
                ))}
              </div>
            </div>

            <Button
              variant="outline"
              size="sm"
              className="mt-4 mx-auto border-white/30 text-white bg-white/10 hover:bg-white/20 hover:text-white"
              onClick={handleExpandClick}
            >
              {isExpanded ? "Hide Details" : "View Details"}
              {isExpanded ? <ChevronUp className="ml-1.5 h-4 w-4" /> : <ChevronDown className="ml-1.5 h-4 w-4" />}
            </Button>

            
          </div>
        </Card>
      </div>
    </div>
  );
}
